import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { IUser } from "Auth/types";
import { useBandContext } from "./BandContext";
import { getUser, userSelectorCreator } from "Bands/state";
import { IBandUser } from "Bands/types";
import { getIdFromPath } from "shared/utils";
import { API_ENDPOINTS } from "shared/constants";

interface UserContextState {
  bandUser?: IBandUser | null;
  user: IUser | null;
}

const UserContextInitState: UserContextState = {
  bandUser: null,
  user: null,
};

const UserContext = createContext(UserContextInitState);

export const UserProvider: React.FC = ({ children }) => {
  const dispatch = useDispatch();

  let { userId: bandUserId } = useParams<{ userId: string }>();
  const { bandUsers } = useBandContext();
  const bandUser = useMemo(
    () => bandUsers.find(({ id }) => bandUserId === id),
    [bandUserId, bandUsers]
  );
  const userId = useMemo(
    () => getIdFromPath(bandUser?.user?.id, API_ENDPOINTS.USERS),
    [bandUser?.user?.id]
  );

  useEffect(() => {
    if (!userId) return;

    dispatch(getUser({ userId }));
  }, [dispatch, userId]);

  const userIdSelector = useMemo(
    () => userSelectorCreator(userId || ""),
    [userId]
  );
  const userAttributes = useSelector(userIdSelector);
  const user: IUser | null = useMemo(() => {
    if (!userAttributes) return null;

    return {
      id: userAttributes.id,
      username: userAttributes.id,
      attributes: userAttributes,
    };
  }, [userAttributes]);

  return (
    <UserContext.Provider
      value={{
        bandUser,
        user,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => useContext(UserContext);
